import { useCallback, useEffect, useRef, useState } from 'react';
import { IconClose, IconLeftCaret, IconRightCaret } from '@belong/icons';
import { ModalBase, Image } from '@belong/ui';
import clsx from 'clsx';

type GalleryCarouselProps = {
  media: string[];
  startIndex?: number;
  onDismiss: () => void;
};

const SWIPE_THRESHOLD = 50;

export function GalleryCarousel({
  media = [],
  startIndex = 0,
  onDismiss,
}: GalleryCarouselProps) {
  const [index, setIndex] = useState(startIndex);
  const touchStartX = useRef<number | null>(null);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setIndex((i) => Math.min(media.length - 1, i + 1));
  }, [media.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.code === 'ArrowLeft') goPrev();
      else if (e.code === 'ArrowRight') goNext();
      else if (e.code === 'Escape') onDismiss();
    }
    document.addEventListener('keyup', onKey);
    return () => document.removeEventListener('keyup', onKey);
  }, [goPrev, goNext, onDismiss]);

  const onTouchStart = (e: React.TouchEvent) => {
    // `!` on the touch-list reads: a touchstart/touchend always carries at
    // least one touch, and both were already dereferenced unconditionally.
    touchStartX.current = e.touches[0]!.clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0]!.clientX - touchStartX.current;
    if (dx > SWIPE_THRESHOLD) goPrev();
    else if (dx < -SWIPE_THRESHOLD) goNext();
    touchStartX.current = null;
  };
  const onTouchCancel = () => {
    touchStartX.current = null;
  };

  const canPrev = index > 0;
  const canNext = index < media.length - 1;

  return (
    <ModalBase
      name="gallery"
      isOpen
      onDismiss={onDismiss}
      overlayClassName="bg-white"
      contentClassName="bg-white w-full h-full"
    >
      <button
        type="button"
        aria-label="Close"
        className="right-2sm top-2sm p-xs absolute z-20 cursor-pointer border-0 bg-white/90"
        onClick={onDismiss}
      >
        <IconClose className="h-2sm w-2sm" />
      </button>
      <div
        className="px-2sm md:px-xl fixed inset-0 flex items-center justify-center overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        <Image
          // `!`: `index` is clamped to `[0, media.length - 1]` by goPrev/goNext.
          src={media[index]!}
          alt=""
          className="max-h-full max-w-full object-contain"
        />

        <button
          type="button"
          aria-label="Previous"
          onClick={goPrev}
          disabled={!canPrev}
          className={clsx(
            'left-2sm p-xs absolute top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer border-0 bg-white/90 lg:block',
            !canPrev && 'opacity-50',
          )}
        >
          <IconLeftCaret width={15} />
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={goNext}
          disabled={!canNext}
          className={clsx(
            'right-2sm p-xs absolute top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer border-0 bg-white/90 lg:block',
            !canNext && 'opacity-50',
          )}
        >
          <IconRightCaret width={15} />
        </button>
      </div>
    </ModalBase>
  );
}
